import { useState, useEffect, useCallback } from "react";
import { useSearchParams } from "react-router-dom";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { Search, Phone, Mail, User, Calendar, ShoppingBag, Award, TrendingUp, Ticket, ChevronRight, X, MessageCircle, AlertTriangle, Clock, Save, FileText, Trash2, Plus } from "lucide-react";
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid } from "recharts";

const formatDate = (d) => d ? new Date(d).toLocaleDateString("id-ID", { day: "2-digit", month: "short", year: "numeric" }) : "-";
const formatDateTime = (d) => d ? new Date(d).toLocaleDateString("id-ID", { day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" }) : "-";
const formatPrice = (p) => new Intl.NumberFormat("id-ID", { style: "currency", currency: "IDR", minimumFractionDigits: 0 }).format(p || 0);
const typeColors = { silver: "bg-gray-700/30 text-gray-300 border-gray-600/30", gold: "bg-yellow-900/30 text-yellow-400 border-yellow-700/30", platinum: "bg-purple-900/30 text-purple-400 border-purple-700/30", prioritas: "bg-[#E60000]/20 text-[#FF4444] border-[#E60000]/30" };
const paymentLabels = { cash: "Cash", qris: "QRIS", edc: "EDC", kupon: "Kupon" };

const INACTIVE_THRESHOLD_DAYS = 60; // 2 bulan

function isInactive(c) {
  return (c.inactive_days || 0) >= INACTIVE_THRESHOLD_DAYS;
}

function inactiveLabel(days) {
  if (!days || days < INACTIVE_THRESHOLD_DAYS) return null;
  const months = Math.floor(days / 30);
  if (months >= 12) return `${Math.floor(months / 12)} thn`;
  return `${months} bln`;
}

function RetentionChart({ data }) {
  if (!data || data.length === 0) return null;
  const chartData = data.map(d => ({
    label: d.month.split("-")[1] + "/" + d.month.split("-")[0].slice(2),
    visits: d.visits,
  }));
  const total = data.reduce((s, d) => s + d.visits, 0);
  const avg = (total / data.length).toFixed(1);
  return (
    <div data-testid="retention-chart">
      <div className="flex items-center justify-between mb-2 text-[10px]">
        <span className="text-gray-500">Total: <span className="text-white font-bold">{total}</span> kunjungan</span>
        <span className="text-gray-500">Rata-rata: <span className="text-white font-bold">{avg}</span>/bulan</span>
      </div>
      <div style={{ width: "100%", height: 180 }}>
        <ResponsiveContainer>
          <BarChart data={chartData} margin={{ top: 10, right: 5, left: -25, bottom: 0 }}>
            <CartesianGrid strokeDasharray="3 3" stroke="#262626" />
            <XAxis dataKey="label" stroke="#666" tick={{ fontSize: 10 }} />
            <YAxis stroke="#666" tick={{ fontSize: 10 }} allowDecimals={false} />
            <Tooltip
              contentStyle={{ background: "#0A0A0A", border: "1px solid #262626", borderRadius: 0, color: "#fff", fontSize: 11 }}
              cursor={{ fill: "rgba(230,0,0,0.05)" }}
              formatter={(val) => [`${val} kunjungan`, ""]}
            />
            <Bar dataKey="visits" fill="#E60000" radius={[2, 2, 0, 0]} />
          </BarChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
}

export default function CustomersPage() {
  const [customers, setCustomers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [detail, setDetail] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [detailOpen, setDetailOpen] = useState(false);
  const [sending, setSending] = useState(null);
  const [filterInactive, setFilterInactive] = useState(false);
  const [reminderOpen, setReminderOpen] = useState(false);
  const [reminderTarget, setReminderTarget] = useState(null);
  const [reminderMsg, setReminderMsg] = useState("");
  const [waTemplates, setWaTemplates] = useState([]);
  const [savingTemplate, setSavingTemplate] = useState(false);
  const [newTemplateName, setNewTemplateName] = useState("");
  const [attachUrl, setAttachUrl] = useState("");
  const [uploading, setUploading] = useState(false);

  const fetchCustomers = useCallback(async (q = "") => {
    setLoading(true);
    try {
      const { data } = await api.get("/customers", { params: q ? { q } : {} });
      setCustomers(data);
    } catch {} finally { setLoading(false); }
  }, []);

  useEffect(() => { fetchCustomers(); }, [fetchCustomers]);

  // Auto-open customer detail from URL ?id=xxx (e.g., from Statistics page)
  const [searchParams, setSearchParams] = useSearchParams();
  useEffect(() => {
    const cid = searchParams.get("id");
    if (cid) {
      openDetail(cid);
      // Clear the URL param after opening
      setSearchParams({}, { replace: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams]);

  const handleSearch = (e) => {
    e.preventDefault();
    fetchCustomers(search);
  };

  const openDetail = async (cid) => {
    setDetailLoading(true);
    setDetailOpen(true);
    try {
      const { data } = await api.get(`/customers/${cid}`);
      setDetail(data);
    } catch { toast.error("Gagal memuat detail"); } finally { setDetailLoading(false); }
  };

  const updateType = async (cid, type) => {
    try {
      await api.put(`/customers/${cid}/type`, { user_type: type });
      toast.success("Tipe pelanggan diperbarui");
      setDetail(prev => prev ? { ...prev, user_type: type } : prev);
      setCustomers(prev => prev.map(c => c.id === cid ? { ...c, user_type: type } : c));
    } catch { toast.error("Gagal memperbarui"); }
  };

  const fetchTemplates = async () => {
    try { const { data } = await api.get("/whatsapp/templates"); setWaTemplates(data); } catch {}
  };

  const parseVariables = (msg, customer) => {
    const name = customer?.name || "Pelanggan";
    const lastVisit = customer?.last_visit ? new Date(customer.last_visit).toLocaleDateString("id-ID", { day: "numeric", month: "long", year: "numeric" }) : "belum tercatat";
    const couponActive = customer?.coupons?.filter(c => !c.is_claimed).length ?? 0;
    const typeMember = (customer?.user_type || "silver").charAt(0).toUpperCase() + (customer?.user_type || "silver").slice(1);
    return msg
      .replace(/\{nama_pelanggan\}/g, name)
      .replace(/\{last_visit\}/g, lastVisit)
      .replace(/\{cupon_active\}/g, String(couponActive))
      .replace(/\{type_member\}/g, typeMember);
  };

  const openReminder = (customer, e) => {
    if (e) e.stopPropagation();
    setReminderTarget(customer);
    const defaultMsg = isInactive(customer)
      ? `Halo {nama_pelanggan}! \u2702\uFE0F\n\nSudah lebih dari 2 bulan nih sejak terakhir kamu potong rambut di *FI Barbershop Sekip* pada *{last_visit}*.\n\nKayaknya sudah saatnya refresh tampilan baru! \uD83D\uDCAA\n\n\u2728 Mampir yuk, ada *promo spesial* buat kamu yang balik lagi.\n\uD83D\uDC88 Capster favorit kamu udah siap bikin kamu makin kece.\n\n\u23F0 Buka setiap hari, jam 10 pagi - 9 malam\n\uD83D\uDCCD Jl. Ampibi, Kec. Kemuning, Palembang\n\nDitunggu ya! \uD83D\uDE4F\n\n_FI Barbershop Sekip_\n\uD83C\uDF10 instagram.com/fibarbershop.sekip`
      : `Halo {nama_pelanggan}! \uD83D\uDC4B\n\nTerima kasih sudah menjadi pelanggan setia *FI Barbershop Sekip*.\n\n\uD83C\uDFC6 Status member kamu: *{type_member}*\n\uD83C\uDFAB Kupon aktif: *{cupon_active}* kupon\n\n\u2728 Ada yang bisa kami bantu? Jangan ragu hubungi kami ya.\n\n\u23F0 Buka setiap hari, jam 10 pagi - 9 malam\n\uD83D\uDCCD Jl. Ampibi, Kec. Kemuning, Palembang\n\n_FI Barbershop Sekip_`;
    setReminderMsg(defaultMsg);
    setNewTemplateName("");
    setAttachUrl("");
    fetchTemplates();
    setReminderOpen(true);
  };

  const applyTemplate = (tpl) => {
    setReminderMsg(tpl.message);
    toast.success(`Template "${tpl.name}" dimuat`);
  };

  const saveAsTemplate = async () => {
    if (!newTemplateName.trim()) { toast.error("Nama template wajib diisi"); return; }
    setSavingTemplate(true);
    try {
      await api.post("/whatsapp/templates", { name: newTemplateName.trim(), message: reminderMsg });
      toast.success("Template tersimpan");
      setNewTemplateName("");
      fetchTemplates();
    } catch { toast.error("Gagal menyimpan template"); }
    finally { setSavingTemplate(false); }
  };

  const deleteTemplate = async (tid) => {
    try { await api.delete(`/whatsapp/templates/${tid}`); toast.success("Template dihapus"); fetchTemplates(); } catch {}
  };

  const handleAttachUpload = async (file) => {
    if (!file) return;
    if (file.size > 4 * 1024 * 1024) { toast.error("Ukuran file maksimal 4MB"); return; }
    setUploading(true);
    const formData = new FormData();
    formData.append("file", file);
    try {
      const { data } = await api.post("/public/upload", formData, { headers: { "Content-Type": "multipart/form-data" } });
      setAttachUrl(data.url);
      toast.success("Foto berhasil diupload");
    } catch { toast.error("Upload gagal"); }
    finally { setUploading(false); }
  };

  const sendReminder = async () => {
    if (!reminderTarget) return;
    const phone = reminderTarget.phone;
    if (!phone) { toast.error("Nomor HP tidak tersedia"); return; }
    const finalMsg = parseVariables(reminderMsg, reminderTarget);
    setSending(reminderTarget.id);
    try {
      const payload = { target: phone, message: finalMsg };
      if (attachUrl) {
        payload.url = attachUrl;
      }
      await api.post("/whatsapp/send", payload);
      toast.success(`Pesan terkirim ke ${reminderTarget.name} (${phone})`);
      setReminderOpen(false);
    } catch (err) {
      const msg = err.response?.data?.detail || "Gagal mengirim. Pastikan Fonnte API sudah dikonfigurasi di Pengaturan.";
      toast.error(msg);
    } finally { setSending(null); }
  };

  const displayed = filterInactive ? customers.filter(c => isInactive(c)) : customers;
  const inactiveCount = customers.filter(c => isInactive(c)).length;

  // Create new member modal state
  const [createOpen, setCreateOpen] = useState(false);
  const [newForm, setNewForm] = useState({ name: "", phone: "", email: "", user_type: "silver" });
  const [creating, setCreating] = useState(false);

  const resetNewForm = () => setNewForm({ name: "", phone: "", email: "", user_type: "silver" });

  const createCustomer = async () => {
    if (!newForm.name.trim() || !newForm.phone.trim()) {
      toast.error("Nama dan No. HP wajib diisi");
      return;
    }
    setCreating(true);
    try {
      const { data } = await api.post("/customers", newForm);
      toast.success("Pelanggan baru berhasil dibuat");
      setCreateOpen(false);
      resetNewForm();
      fetchCustomers(search);
      // Open detail of the newly created customer
      openDetail(data.id);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Gagal membuat pelanggan");
    } finally {
      setCreating(false);
    }
  };

  return (
    <div className="p-6 md:p-8" data-testid="customers-page">
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 mb-6">
        <div>
          <h1 className="font-heading text-3xl font-bold uppercase tracking-tight text-white">Pelanggan</h1>
          <p className="text-gray-400 text-sm mt-1">{customers.length} pelanggan terdaftar</p>
        </div>
        <div className="flex items-center gap-2 w-full sm:w-auto flex-wrap">
          <Button type="button" onClick={() => { resetNewForm(); setCreateOpen(true); }} className="bg-[#E60000] hover:bg-[#FF1A1A] text-white font-bold uppercase rounded-none h-10 px-4 flex items-center gap-1.5" data-testid="add-customer-btn">
            <Plus size={16} /> Tambah Pelanggan
          </Button>
          <form onSubmit={handleSearch} className="flex items-center gap-2 flex-1 sm:flex-initial">
            <div className="relative flex-1 sm:w-72">
              <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500" />
              <Input
                className="pl-9 bg-[#0F0F0F] border-[#262626] text-white h-10 rounded-none focus:border-[#E60000]"
                placeholder="Cari nama, HP (081xx / 628xx), email..."
                value={search}
                onChange={e => setSearch(e.target.value)}
                data-testid="customer-search-input"
              />
            </div>
            <Button type="submit" className="bg-[#262626] hover:bg-[#333] text-white font-bold uppercase rounded-none h-10 px-4" data-testid="customer-search-btn">
              Cari
            </Button>
            {search && (
              <Button type="button" variant="ghost" className="text-gray-400 hover:text-white h-10 px-2 rounded-none" onClick={() => { setSearch(""); fetchCustomers(); }}>
                <X size={16} />
              </Button>
            )}
          </form>
        </div>
      </div>

      {/* Inactive Alert Banner */}
      {inactiveCount > 0 && (
        <div className="mb-5 bg-orange-950/30 border border-orange-800/30 p-4 flex items-center justify-between" data-testid="inactive-banner">
          <div className="flex items-center gap-3">
            <div className="w-9 h-9 bg-orange-900/40 flex items-center justify-center shrink-0">
              <AlertTriangle size={18} className="text-orange-400" />
            </div>
            <div>
              <p className="text-orange-300 text-sm font-bold">{inactiveCount} pelanggan tidak aktif</p>
              <p className="text-orange-400/70 text-xs">Belum berkunjung selama 2 bulan atau lebih. Kirim pengingat via WhatsApp.</p>
            </div>
          </div>
          <Button
            onClick={() => setFilterInactive(!filterInactive)}
            className={`rounded-none h-9 text-xs font-bold uppercase ${filterInactive ? "bg-orange-600 hover:bg-orange-700 text-white" : "bg-orange-900/40 hover:bg-orange-900/60 text-orange-300 border border-orange-800/40"}`}
            data-testid="filter-inactive-btn"
          >
            {filterInactive ? "Tampilkan Semua" : "Tampilkan Tidak Aktif"}
          </Button>
        </div>
      )}

      {loading ? (
        <div className="flex items-center justify-center py-20"><div className="w-8 h-8 border-2 border-[#E60000] border-t-transparent rounded-full animate-spin" /></div>
      ) : displayed.length === 0 ? (
        <div className="text-center py-20 bg-[#0F0F0F] border border-[#262626]">
          <User size={48} className="text-gray-700 mx-auto mb-4" />
          <p className="text-gray-400">{search ? "Tidak ditemukan pelanggan dengan pencarian tersebut" : filterInactive ? "Tidak ada pelanggan tidak aktif" : "Belum ada pelanggan terdaftar"}</p>
        </div>
      ) : (
        <div className="bg-[#0F0F0F] border border-[#262626] overflow-x-auto" data-testid="customers-table">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-[#262626]">
                <th className="text-left px-5 py-3 text-xs font-bold uppercase text-gray-500">Pelanggan</th>
                <th className="text-left px-5 py-3 text-xs font-bold uppercase text-gray-500">No. HP</th>
                <th className="text-left px-5 py-3 text-xs font-bold uppercase text-gray-500">Tipe</th>
                <th className="text-left px-5 py-3 text-xs font-bold uppercase text-gray-500">Kunjungan</th>
                <th className="text-left px-5 py-3 text-xs font-bold uppercase text-gray-500">Kunjungan Terakhir</th>
                <th className="text-right px-5 py-3 text-xs font-bold uppercase text-gray-500">Aksi</th>
              </tr>
            </thead>
            <tbody>
              {displayed.map(c => {
                const inactive = isInactive(c);
                const inactiveText = inactiveLabel(c.inactive_days);
                return (
                  <tr key={c.id} className={`border-b border-[#262626]/50 hover:bg-[#1A1A1A] cursor-pointer ${inactive ? "bg-orange-950/5" : ""}`} onClick={() => openDetail(c.id)} data-testid={`customer-row-${c.id}`}>
                    <td className="px-5 py-3">
                      <p className="text-white font-semibold">{c.name}</p>
                      <p className="text-gray-500 text-xs flex items-center gap-1"><Mail size={10} />{c.email}</p>
                    </td>
                    <td className="px-5 py-3">
                      <p className="text-gray-300 text-sm flex items-center gap-1"><Phone size={12} />{c.phone}</p>
                    </td>
                    <td className="px-5 py-3">
                      <Badge className={`border text-[10px] uppercase ${typeColors[c.user_type] || typeColors.silver}`}>{c.user_type || "silver"}</Badge>
                    </td>
                    <td className="px-5 py-3">
                      <span className="text-white font-bold">{c.visit_count || 0}</span>
                      <span className="text-gray-500 text-xs ml-1">kali</span>
                    </td>
                    <td className="px-5 py-3">
                      <div className="flex items-center gap-2">
                        {c.last_visit ? (
                          <span className={`text-xs ${inactive ? "text-orange-400" : "text-gray-500"}`}>{formatDate(c.last_visit)}</span>
                        ) : (
                          <span className="text-gray-600 text-xs italic">Belum ada</span>
                        )}
                        {inactive && (
                          <Button
                            size="sm"
                            onClick={(e) => openReminder(c, e)}
                            disabled={sending === c.id}
                            className="bg-green-700 hover:bg-green-600 text-white rounded-none h-6 text-[10px] font-bold uppercase px-2"
                            data-testid={`remind-btn-${c.id}`}
                          >
                            <MessageCircle size={11} className="mr-1" />
                            {sending === c.id ? "..." : "Ingatkan"}
                          </Button>
                        )}
                      </div>
                    </td>
                    <td className="px-5 py-3 text-right">
                      <ChevronRight size={16} className="text-gray-600 inline" />
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {/* Detail Dialog */}
      <Dialog open={detailOpen} onOpenChange={setDetailOpen}>
        <DialogContent className="bg-[#0F0F0F] border-[#262626] text-white max-w-2xl rounded-none max-h-[85vh] overflow-y-auto">
          {detailLoading ? (
            <div className="flex items-center justify-center py-12"><div className="w-8 h-8 border-2 border-[#E60000] border-t-transparent rounded-full animate-spin" /></div>
          ) : detail ? (
            <>
              <DialogHeader>
                <div className="flex items-center gap-3">
                  <DialogTitle className="font-heading text-xl font-bold uppercase">{detail.name}</DialogTitle>
                  {isInactive(detail) && (
                    <Badge className="bg-orange-900/30 text-orange-400 border border-orange-800/30 text-[10px] uppercase" data-testid="detail-inactive-badge">
                      <AlertTriangle size={10} className="mr-1" /> Tidak aktif {inactiveLabel(detail.inactive_days)}
                    </Badge>
                  )}
                </div>
                <DialogDescription className="text-gray-400 text-sm">Detail pelanggan & riwayat kunjungan</DialogDescription>
              </DialogHeader>

              <div data-testid="customer-detail">
                {/* Inactive Reminder Banner */}
                {isInactive(detail) && (
                  <div className="mb-4 bg-orange-950/30 border border-orange-800/30 p-3 flex items-center justify-between" data-testid="detail-inactive-alert">
                    <div className="flex items-center gap-2">
                      <AlertTriangle size={16} className="text-orange-400 shrink-0" />
                      <div>
                        <p className="text-orange-300 text-xs font-bold">Pelanggan tidak aktif selama {inactiveLabel(detail.inactive_days)}</p>
                        <p className="text-orange-400/60 text-[10px]">
                          Kunjungan terakhir: {detail.last_visit ? formatDate(detail.last_visit) : "Belum pernah"}
                        </p>
                      </div>
                    </div>
                    <Button
                      size="sm"
                      onClick={() => openReminder(detail)}
                      disabled={sending === detail.id}
                      className="bg-green-700 hover:bg-green-600 text-white rounded-none h-8 text-[10px] font-bold uppercase px-3 shrink-0"
                      data-testid="detail-remind-btn"
                    >
                      <MessageCircle size={14} className="mr-1" />
                      {sending === detail.id ? "Mengirim..." : "Ingatkan via WhatsApp"}
                    </Button>
                  </div>
                )}

                {/* Info Grid */}
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-5">
                  <div className="bg-[#0A0A0A] border border-[#1A1A1A] p-3">
                    <p className="text-[10px] text-gray-500 uppercase font-bold flex items-center gap-1"><Phone size={9} /> HP</p>
                    <p className="text-white text-sm mt-0.5 font-semibold">{detail.phone}</p>
                  </div>
                  <div className="bg-[#0A0A0A] border border-[#1A1A1A] p-3">
                    <p className="text-[10px] text-gray-500 uppercase font-bold flex items-center gap-1"><Mail size={9} /> Email</p>
                    <p className="text-white text-sm mt-0.5 truncate">{detail.email}</p>
                  </div>
                  <div className="bg-[#0A0A0A] border border-[#1A1A1A] p-3">
                    <p className="text-[10px] text-gray-500 uppercase font-bold flex items-center gap-1"><ShoppingBag size={9} /> Kunjungan</p>
                    <p className="text-white text-sm mt-0.5 font-bold">{detail.visit_count || 0} kali</p>
                  </div>
                  <div className="bg-[#0A0A0A] border border-[#1A1A1A] p-3">
                    <p className="text-[10px] text-gray-500 uppercase font-bold flex items-center gap-1"><Clock size={9} /> Terakhir</p>
                    <p className={`text-sm mt-0.5 ${isInactive(detail) ? "text-orange-400 font-semibold" : "text-white"}`}>
                      {detail.last_visit ? formatDate(detail.last_visit) : "Belum ada"}
                    </p>
                  </div>
                </div>

                {/* Type & Loyalty */}
                <div className="flex items-center gap-4 mb-5 p-3 bg-[#0A0A0A] border border-[#1A1A1A]">
                  <div className="flex-1">
                    <p className="text-[10px] text-gray-500 uppercase font-bold flex items-center gap-1 mb-1"><Award size={9} /> Tipe Member</p>
                    <Select value={detail.user_type || "silver"} onValueChange={v => updateType(detail.id, v)}>
                      <SelectTrigger className="bg-[#1A1A1A] border-[#262626] text-white h-8 rounded-none w-32" data-testid="customer-type-select">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent className="bg-[#1A1A1A] border-[#262626]">
                        <SelectItem value="silver" className="text-white">Silver</SelectItem>
                        <SelectItem value="gold" className="text-white">Gold</SelectItem>
                        <SelectItem value="platinum" className="text-white">Platinum</SelectItem>
                        <SelectItem value="prioritas" className="text-white">Prioritas</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="flex-1">
                    <p className="text-[10px] text-gray-500 uppercase font-bold flex items-center gap-1 mb-1"><Ticket size={9} /> Loyalitas</p>
                    <p className="text-white text-sm">
                      <span className="font-bold">{detail.visit_count ? detail.visit_count % 5 : 0}</span>/5 kupon
                      {detail.visit_count && detail.visit_count % 5 === 0 && detail.visit_count > 0 && (
                        <Badge className="ml-2 bg-green-900/30 text-green-400 border border-green-700/30 text-[9px]">Gratis!</Badge>
                      )}
                    </p>
                  </div>
                  <div className="flex-1">
                    <p className="text-[10px] text-gray-500 uppercase font-bold mb-1">Kupon Aktif</p>
                    <p className="text-white text-sm font-bold">{detail.coupons?.filter(c => !c.is_claimed).length || 0} kupon</p>
                  </div>
                </div>

                {/* Retention Chart */}
                <div className="mb-5 p-4 bg-[#0A0A0A] border border-[#1A1A1A]">
                  <p className="text-xs text-gray-400 uppercase font-bold flex items-center gap-1 mb-3"><TrendingUp size={12} /> Retensi Kunjungan (12 Bulan Terakhir)</p>
                  <RetentionChart data={detail.retention_chart} />
                </div>

                {/* Transactions */}
                <div>
                  <p className="text-xs text-gray-400 uppercase font-bold flex items-center gap-1 mb-3"><ShoppingBag size={12} /> Riwayat Transaksi ({detail.recent_transactions?.length || 0})</p>
                  {detail.recent_transactions?.length > 0 ? (
                    <div className="space-y-2 max-h-60 overflow-y-auto">
                      {detail.recent_transactions.map(tx => (
                        <div key={tx.id} className="bg-[#0A0A0A] border border-[#1A1A1A] p-3 flex items-center justify-between" data-testid={`tx-row-${tx.id}`}>
                          <div className="flex-1">
                            <p className="text-white text-xs font-semibold font-mono">{tx.transaction_code}</p>
                            <p className="text-gray-500 text-[10px] mt-0.5">
                              {tx.items?.map(i => i.name).join(", ")}
                            </p>
                          </div>
                          <div className="text-right shrink-0 ml-4">
                            <p className="text-[#E60000] font-bold text-sm">{formatPrice(tx.total)}</p>
                            <div className="flex items-center gap-2 justify-end mt-0.5">
                              <Badge className="text-[8px] bg-[#1A1A1A] border border-[#262626] text-gray-400">{paymentLabels[tx.payment_method] || tx.payment_method}</Badge>
                              <span className="text-gray-600 text-[10px]">{formatDateTime(tx.created_at)}</span>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <p className="text-gray-600 text-sm text-center py-6">Belum ada transaksi</p>
                  )}
                </div>

                {/* Kirim WhatsApp Button */}
                <div className="mt-5 pt-4 border-t border-[#1A1A1A]">
                  <Button
                    onClick={() => openReminder(detail)}
                    className="w-full bg-green-700 hover:bg-green-600 text-white font-bold uppercase rounded-none h-10"
                    data-testid="detail-send-wa-btn"
                  >
                    <MessageCircle size={16} className="mr-2" /> Kirim WhatsApp
                  </Button>
                </div>
              </div>
            </>
          ) : null}
        </DialogContent>
      </Dialog>

      {/* WhatsApp Message Popup */}
      <Dialog open={reminderOpen} onOpenChange={setReminderOpen}>
        <DialogContent className="bg-[#0F0F0F] border-[#262626] text-white max-w-lg rounded-none max-h-[85vh] overflow-y-auto" data-testid="reminder-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading text-lg font-bold uppercase flex items-center gap-2">
              <MessageCircle size={18} className="text-green-500" /> Kirim WhatsApp
            </DialogTitle>
            <DialogDescription className="text-gray-400 text-sm">
              Kirim pesan ke <span className="text-white font-semibold">{reminderTarget?.name}</span> ({reminderTarget?.phone})
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            {/* Variable Tags */}
            <div>
              <label className="text-gray-500 text-[10px] font-bold uppercase block mb-1.5">Sisipkan Variabel</label>
              <div className="flex flex-wrap gap-1.5">
                {[
                  { tag: "{nama_pelanggan}", label: "Nama" },
                  { tag: "{last_visit}", label: "Kunjungan Terakhir" },
                  { tag: "{cupon_active}", label: "Kupon Aktif" },
                  { tag: "{type_member}", label: "Tipe Member" },
                ].map(v => (
                  <button
                    key={v.tag}
                    type="button"
                    onClick={() => setReminderMsg(prev => prev + v.tag)}
                    className="bg-[#1A1A1A] border border-[#262626] hover:border-green-700 text-gray-300 hover:text-green-400 px-2.5 py-1 text-[10px] font-mono transition-colors"
                    data-testid={`var-${v.tag}`}
                  >
                    {v.tag} <span className="text-gray-600 font-sans ml-1">{v.label}</span>
                  </button>
                ))}
              </div>
            </div>

            {/* Template Selector */}
            {waTemplates.length > 0 && (
              <div>
                <label className="text-gray-500 text-[10px] font-bold uppercase block mb-1.5">Pilih Template</label>
                <div className="space-y-1 max-h-28 overflow-y-auto">
                  {waTemplates.map(tpl => (
                    <div key={tpl.id} className="flex items-center justify-between bg-[#1A1A1A] border border-[#262626] hover:border-[#333] px-3 py-1.5 group">
                      <button type="button" onClick={() => applyTemplate(tpl)} className="flex-1 text-left text-xs text-gray-300 hover:text-white truncate flex items-center gap-1.5" data-testid={`tpl-${tpl.id}`}>
                        <FileText size={12} className="text-gray-500 shrink-0" />
                        <span className="font-semibold">{tpl.name}</span>
                      </button>
                      <button type="button" onClick={() => deleteTemplate(tpl.id)} className="text-gray-600 hover:text-red-400 opacity-0 group-hover:opacity-100 transition-opacity p-0.5" data-testid={`del-tpl-${tpl.id}`}>
                        <Trash2 size={12} />
                      </button>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Message */}
            <div>
              <label className="text-gray-500 text-[10px] font-bold uppercase block mb-1.5">Pesan</label>
              <textarea
                className="w-full bg-[#1A1A1A] border border-[#262626] text-white p-3 rounded-none min-h-[160px] text-sm focus:border-green-700 focus:outline-none font-[inherit] leading-relaxed"
                value={reminderMsg}
                onChange={e => setReminderMsg(e.target.value)}
                data-testid="reminder-message"
              />
              {/* Preview */}
              {reminderTarget && reminderMsg.includes("{") && (
                <div className="mt-2 bg-[#0A0A0A] border border-[#1A1A1A] p-3">
                  <p className="text-[10px] text-gray-500 uppercase font-bold mb-1">Preview</p>
                  <p className="text-gray-300 text-xs whitespace-pre-line leading-relaxed">{parseVariables(reminderMsg, reminderTarget)}</p>
                </div>
              )}
            </div>

            {/* Attachment */}
            <div>
              <label className="text-gray-500 text-[10px] font-bold uppercase block mb-1.5">Lampiran Foto / Gambar</label>
              {attachUrl ? (
                <div className="flex items-start gap-3 bg-[#1A1A1A] border border-[#262626] p-2.5">
                  <img src={attachUrl} alt="Attachment" className="w-16 h-16 object-cover border border-[#262626] shrink-0" />
                  <div className="flex-1 min-w-0">
                    <p className="text-xs text-green-400 font-semibold truncate">Foto terlampir</p>
                    <p className="text-[10px] text-gray-500 truncate mt-0.5">{attachUrl}</p>
                  </div>
                  <button type="button" onClick={() => setAttachUrl("")} className="text-gray-500 hover:text-red-400 shrink-0 p-1" data-testid="remove-attach">
                    <X size={14} />
                  </button>
                </div>
              ) : (
                <div className="flex items-center gap-2">
                  <label className="flex-1 flex items-center gap-2 cursor-pointer bg-[#1A1A1A] border border-dashed border-[#333] hover:border-green-700/50 p-3 transition-colors" data-testid="attach-upload-label">
                    <Plus size={16} className="text-gray-500 shrink-0" />
                    <span className="text-xs text-gray-400">{uploading ? "Mengupload..." : "Upload foto (maks 4MB)"}</span>
                    <input type="file" className="hidden" accept="image/*" onChange={e => handleAttachUpload(e.target.files[0])} disabled={uploading} data-testid="attach-upload-input" />
                  </label>
                  <span className="text-gray-600 text-[10px]">atau</span>
                  <Input
                    className="flex-1 bg-[#1A1A1A] border-[#262626] text-white h-10 rounded-none text-xs focus:border-green-700"
                    placeholder="Tempel URL gambar..."
                    value={attachUrl}
                    onChange={e => setAttachUrl(e.target.value)}
                    data-testid="attach-url-input"
                  />
                </div>
              )}
              <p className="text-gray-600 text-[10px] mt-1">Opsional. Gambar akan dikirim bersama pesan via WhatsApp.</p>
            </div>

            {/* Save as Template */}
            <div className="flex items-center gap-2">
              <Input
                className="flex-1 bg-[#1A1A1A] border-[#262626] text-white h-8 rounded-none text-xs focus:border-green-700"
                placeholder="Nama template (opsional)..."
                value={newTemplateName}
                onChange={e => setNewTemplateName(e.target.value)}
                data-testid="template-name-input"
              />
              <Button
                type="button"
                size="sm"
                onClick={saveAsTemplate}
                disabled={savingTemplate || !newTemplateName.trim()}
                className="bg-[#1A1A1A] border border-[#262626] hover:border-green-700 text-gray-400 hover:text-green-400 rounded-none h-8 text-[10px] font-bold uppercase px-3"
                data-testid="save-template-btn"
              >
                <Save size={12} className="mr-1" /> {savingTemplate ? "..." : "Simpan Template"}
              </Button>
            </div>

            {/* Actions */}
            <div className="flex gap-2 pt-1">
              <Button
                onClick={sendReminder}
                disabled={sending === reminderTarget?.id || !reminderMsg.trim()}
                className="flex-1 bg-green-700 hover:bg-green-600 text-white font-bold uppercase rounded-none h-10"
                data-testid="send-reminder-btn"
              >
                <MessageCircle size={16} className="mr-2" />
                {sending === reminderTarget?.id ? "Mengirim..." : attachUrl ? "Kirim dengan Foto" : "Kirim WhatsApp"}
              </Button>
              <Button
                onClick={() => setReminderOpen(false)}
                variant="ghost"
                className="text-gray-400 hover:text-white rounded-none h-10 px-4"
              >
                Batal
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Create New Member Modal */}
      <Dialog open={createOpen} onOpenChange={setCreateOpen}>
        <DialogContent className="bg-[#0A0A0A] border-[#262626] text-white max-w-md rounded-none" data-testid="create-customer-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading text-xl font-bold uppercase flex items-center gap-2">
              <Plus size={18} className="text-[#E60000]" /> Tambah Pelanggan Baru
            </DialogTitle>
            <DialogDescription className="text-gray-500 text-xs">Daftarkan pelanggan baru. Nama & No. HP wajib diisi.</DialogDescription>
          </DialogHeader>
          <div className="space-y-4 mt-2">
            <div>
              <label className="text-[10px] font-bold uppercase tracking-wider text-gray-500 mb-1 block">Nama Lengkap <span className="text-[#E60000]">*</span></label>
              <Input
                className="bg-[#1A1A1A] border-[#262626] text-white h-10 rounded-none"
                placeholder="Contoh: Budi Santoso"
                value={newForm.name}
                onChange={e => setNewForm(f => ({ ...f, name: e.target.value }))}
                data-testid="new-customer-name"
              />
            </div>
            <div>
              <label className="text-[10px] font-bold uppercase tracking-wider text-gray-500 mb-1 block">No. HP (WhatsApp) <span className="text-[#E60000]">*</span></label>
              <Input
                className="bg-[#1A1A1A] border-[#262626] text-white h-10 rounded-none font-mono"
                placeholder="08123456789"
                value={newForm.phone}
                onChange={e => setNewForm(f => ({ ...f, phone: e.target.value.replace(/\D/g, "") }))}
                data-testid="new-customer-phone"
              />
            </div>
            <div>
              <label className="text-[10px] font-bold uppercase tracking-wider text-gray-500 mb-1 block">Email (Opsional)</label>
              <Input
                type="email"
                className="bg-[#1A1A1A] border-[#262626] text-white h-10 rounded-none"
                placeholder="nama@email.com"
                value={newForm.email}
                onChange={e => setNewForm(f => ({ ...f, email: e.target.value }))}
                data-testid="new-customer-email"
              />
            </div>
            <div>
              <label className="text-[10px] font-bold uppercase tracking-wider text-gray-500 mb-1 block">Tipe Member</label>
              <Select value={newForm.user_type} onValueChange={v => setNewForm(f => ({ ...f, user_type: v }))}>
                <SelectTrigger className="bg-[#1A1A1A] border-[#262626] text-white h-10 rounded-none" data-testid="new-customer-type"><SelectValue /></SelectTrigger>
                <SelectContent className="bg-[#1A1A1A] border-[#262626]">
                  <SelectItem value="silver" className="text-white">Silver</SelectItem>
                  <SelectItem value="gold" className="text-white">Gold</SelectItem>
                  <SelectItem value="platinum" className="text-white">Platinum</SelectItem>
                  <SelectItem value="prioritas" className="text-white">Prioritas</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="flex gap-2 pt-2 border-t border-[#262626]">
              <Button
                onClick={createCustomer}
                disabled={creating || !newForm.name.trim() || !newForm.phone.trim()}
                className="flex-1 bg-[#E60000] hover:bg-[#FF1A1A] text-white font-bold uppercase tracking-wider rounded-none h-10 disabled:opacity-40"
                data-testid="submit-new-customer-btn"
              >
                {creating ? "Menyimpan..." : "Simpan & Buka"}
              </Button>
              <Button
                type="button"
                onClick={() => setCreateOpen(false)}
                variant="ghost"
                className="text-gray-400 hover:text-white rounded-none h-10 px-4"
                data-testid="cancel-new-customer-btn"
              >
                Batal
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
